import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../services/api";
import { getEcho } from "../services/echo";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700 border border-yellow-200",
  scheduled: "bg-blue-100 text-blue-700 border border-blue-200",
  ready_for_dispatch: "bg-purple-100 text-purple-700 border border-purple-200",
  assigned: "bg-blue-100 text-blue-700 border border-blue-200",
  picked: "bg-purple-100 text-purple-700 border border-purple-200",
  in_transit: "bg-orange-100 text-orange-700 border border-orange-200",
  delivered: "bg-green-100 text-green-700 border border-green-200",
  cancelled: "bg-red-100 text-red-700 border border-red-200",
  attempted: "bg-amber-100 text-amber-700 border border-amber-200",
};

const isOverdue = (order) => {
  if (order.status !== "pending") return false;
  const created = new Date(order.created_at);
  const diff = (Date.now() - created.getTime()) / 1000 / 60; // minutes
  return diff > 30;
};

const STATUS_FLOW = ["pending", "scheduled", "ready_for_dispatch", "assigned", "picked", "in_transit", "delivered"];

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [toast, setToast] = useState(null);
  const [createForm, setCreateForm] = useState({
    customer_name: "",
    customer_phone: "",
    address: "",
    items: "",
    delivery_fee: "",
    notes: "",
    scheduled_at: "",
  });
  const [isScheduled, setIsScheduled] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedRider, setSelectedRider] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders");
      setOrders(res.data?.data || res.data || []);
    } catch {
      showToast("Failed to load orders.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRiders = useCallback(async () => {
    try {
      const res = await api.get("/riders");
      setRiders(res.data.filter((r) => r.is_active));
    } catch { }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchRiders();
  }, [fetchOrders, fetchRiders]);

  useEffect(() => {
    if (!user?.shop_id) return;

    const echo = getEcho();

    if (!echo) {
      setIsLive(false);
      return;
    }

    console.log("🔌 Subscribing to shop channel:", user.shop_id);

    const channel = echo.private(`shop.${user.shop_id}`);

    channel.listen(".order.updated", (e) => {
      setOrders((prev) =>
        prev.map((o) =>
          o.id === e.order_id
            ? {
              ...o,
              status: e.status,
              assigned_rider_id: e.rider_id,
              customer_confirmed_at: e.customer_confirmed_at,
              customer_rating: e.customer_rating,
              customer_feedback: e.customer_feedback,
              is_scheduled: e.is_scheduled,
              scheduled_at: e.scheduled_at,
            }
            : o
        )
      );
      setShowDetail((prev) =>
        prev?.id === e.order_id
          ? {
            ...prev,
            status: e.status,
            customer_confirmed_at: e.customer_confirmed_at,
            customer_rating: e.customer_rating,
            customer_feedback: e.customer_feedback,
            is_scheduled: e.is_scheduled,
            scheduled_at: e.scheduled_at,
          }
          : prev
      );
    });

    channel.listen(".rider.location", (e) => {
      if (!e.order_id) return;
      setOrders((prev) =>
        prev.map((o) =>
          o.id === e.order_id
            ? {
              ...o,
              rider_location: {
                latitude: e.latitude,
                longitude: e.longitude,
                recorded_at: e.recorded_at,
              },
            }
            : o
        )
      );
    });

    channel.listenToAll((event, data) => {
      console.log("📡 Pusher event:", event, data);
    });

    setIsLive(true);

    return () => {
      echo.leave(`shop.${user.shop_id}`);
    };
  }, [user?.shop_id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError("");
    setCreateLoading(true);
    try {
      const res = await api.post("/orders", createForm);
      setOrders((prev) => [res.data, ...prev]);
      setShowCreate(false);
      setCreateForm({
        customer_name: "",
        customer_phone: "",
        address: "",
        items: "",
        delivery_fee: "",
        notes: "",
        scheduled_at: "",
      });
      setIsScheduled(false);
      showToast("Order created successfully.");
    } catch (err) {
      setCreateError(
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {}).flat().join(" ") ||
        "Failed to create order."
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedRider || !showAssign) return;
    setAssignLoading(true);
    try {
      await api.post("/assign-rider", { order_id: showAssign.id, rider_id: selectedRider });
      setOrders((prev) =>
        prev.map((o) =>
          o.id === showAssign.id
            ? {
              ...o,
              status: "assigned",
              assigned_rider_id: Number(selectedRider),
              rider: riders.find((r) => r.id === Number(selectedRider)),
            }
            : o
        )
      );
      setShowAssign(null);
      setSelectedRider("");
      showToast("Driver assigned successfully.");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to assign driver.", "error");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUpdateStatus = async (order, newStatus) => {
    setActionLoading(`status-${order.id}`);
    try {
      await api.post("/update-status", { order_id: order.id, status: newStatus });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o)));
      showToast(`Order marked as ${newStatus.replace("_", " ")}.`);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update status.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (order) => {
    if (!window.confirm("Cancel this order?")) return;
    setActionLoading(`cancel-${order.id}`);
    try {
      await api.post("/orders/cancel", { order_id: order.id });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: "cancelled" } : o)));
      showToast("Order cancelled.");
    } catch (err) {
      showToast(err.response?.data?.message || "Cannot cancel this order.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const copyTrackingLink = (token) => {
    const url = `${window.location.origin}/track/${token}`;
    navigator.clipboard.writeText(url);
    showToast("Tracking link copied!");
  };

  const filtered = orders.filter((o) => {
    const matchStatus = statusFilter === "all" || o.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      o.tracking_token?.toLowerCase().includes(q) ||
      o.customer_name?.toLowerCase().includes(q) ||
      o.customer_phone?.includes(q) ||
      o.address?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const counts = {
    all: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    scheduled: orders.filter((o) => o.status === "scheduled").length,
    ready_for_dispatch: orders.filter((o) => o.status === "ready_for_dispatch").length,
    assigned: orders.filter((o) => o.status === "assigned").length,
    in_transit: orders.filter((o) => o.status === "in_transit").length,
    attempted: orders.filter((o) => o.status === "attempted").length,
    delivered: orders.filter((o) => o.status === "delivered").length,
    cancelled: orders.filter((o) => o.status === "cancelled").length,
  };

  const nextStatus = (current) => {
    const idx = STATUS_FLOW.indexOf(current);
    return idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  };

  return (
    <Layout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
            }`}
        >
          {toast.type === "error" ? "❌" : "✅"} {toast.message}
        </div>
      )}

      {orders.filter(isOverdue).length > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-5 py-3.5 rounded-xl mb-4">
          <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-sm font-semibold">
            {orders.filter(isOverdue).length} order{orders.filter(isOverdue).length > 1 ? "s have" : " has"} been unassigned for over 30 minutes — assign a driver now.
          </p>
        </div>
      )}

      <div className="p-6 max-w-7xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                Orders
              </h1>
              <p className="text-gray-500 text-sm mt-1">{orders.length} total orders</p>
            </div>
            <div
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${isLive ? "bg-green-50 text-green-600 border-green-200" : "bg-gray-100 text-gray-400 border-gray-200"
                }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
              {isLive ? "Live" : "Offline"}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchOrders}
              className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
              Refresh
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl shadow-md shadow-orange-200 transition-all text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              New Order
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 15.803a7.5 7.5 0 0 0 10.607 0z"
              />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by token, customer name, phone or address..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-1.5 bg-gray-100 rounded-xl p-1 overflow-x-auto">
            {Object.entries(counts).map(([s, count]) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-all ${statusFilter === s ? "bg-orange-500 text-white shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
              >
                {s.replace("_", " ")} ({count})
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading orders...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <p className="text-4xl mb-3">📦</p>
              <p className="font-medium text-gray-600">No orders found</p>
              <p className="text-sm mt-1">
                {search || statusFilter !== "all"
                  ? "Try adjusting your search or filter."
                  : "Create your first order to get started."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    {["Tracking", "Customer", "Address", "Status", "Driver", "Fee", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="text-left px-5 py-3.5 text-gray-500 font-semibold text-xs uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((order) => {
                    const next = nextStatus(order.status);
                    const isActing =
                      actionLoading === `status-${order.id}` || actionLoading === `cancel-${order.id}`;
                    return (
                      <tr key={order.id} className={`transition-colors ${
                        isOverdue(order)
                          ? "bg-red-50/50 hover:bg-red-50"
                          : "hover:bg-gray-50/50"
                      }`}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">
                              {order.tracking_token}
                            </span>
                            {isOverdue(order) && (
                              <span className="inline-flex items-center gap-1 bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full border border-red-200">
                                ⚠️ Overdue
                              </span>
                            )}
                            <button
                              onClick={() => copyTrackingLink(order.tracking_token)}
                              className="text-gray-400 hover:text-orange-500 transition-colors"
                              title="Copy tracking link"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <p className="font-medium text-gray-900">{order.customer_name}</p>
                          <p className="text-gray-400 text-xs">{order.customer_phone}</p>
                        </td>

                        <td className="px-5 py-4 text-gray-500 text-xs max-w-[160px]">
                          <p className="truncate">{order.address}</p>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                              STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {order.status?.replace(/_/g, " ")}
                          </span>
                          {order.is_scheduled && order.scheduled_at && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-blue-600 font-semibold">
                                🗓️ {new Date(order.scheduled_at).toLocaleString("en-KE", {
                                  month: "short", day: "numeric",
                                  hour: "2-digit", minute: "2-digit",
                                })}
                              </span>
                            </div>
                          )}
                          {order.customer_confirmed_at && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-green-600 font-semibold">✓ Customer confirmed</span>
                            </div>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          {order.rider ? (
                            <p className="text-gray-700 text-xs font-medium">{order.rider.name}</p>
                          ) : (
                            <button
                              onClick={() => {
                                setShowAssign(order);
                                setSelectedRider("");
                              }}
                              disabled={order.status === "cancelled"}
                              className="text-xs text-orange-500 hover:text-orange-600 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Assign Driver
                            </button>
                          )}
                        </td>

                        <td className="px-5 py-4 text-gray-900 font-medium text-xs">
                          KES {Number(order.delivery_fee || 0).toLocaleString()}
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            {next && order.status !== "cancelled" && (
                              <button
                                onClick={() => handleUpdateStatus(order, next)}
                                disabled={isActing}
                                className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-100 font-semibold px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50 capitalize whitespace-nowrap"
                              >
                                {isActing ? "..." : `→ ${next.replace("_", " ")}`}
                              </button>
                            )}
                            {["pending", "scheduled", "assigned"].includes(order.status) && (
                              <button
                                onClick={() => handleCancel(order)}
                                disabled={isActing}
                                className="text-xs bg-red-50 text-red-500 hover:bg-red-100 border border-red-100 font-semibold px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            )}
                            <button
                              onClick={() => setShowDetail(order)}
                              className="text-xs text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                              title="View details"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.641 0-8.573-3.007-9.963-7.178z"
                                />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && filtered.length > 0 && (
          <p className="text-gray-400 text-xs text-right mt-3">
            Showing {filtered.length} of {orders.length} orders
          </p>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                New Delivery Order
              </h2>
              <button
                onClick={() => {
                  setShowCreate(false);
                  setCreateError("");
                  setIsScheduled(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {createError && (
                <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl p-3">{createError}</div>
              )}

              {[
                { key: "customer_name", label: "Customer Name", placeholder: "e.g. Jane Wanjiru" },
                { key: "customer_phone", label: "Customer Phone", placeholder: "07XXXXXXXX" },
                { key: "address", label: "Delivery Address", placeholder: "e.g. Westlands, Nairobi" },
                { key: "items", label: "Order Items", placeholder: "e.g. 2x Pilau, 1x Juice, 1x Water" },
                { key: "delivery_fee", label: "Delivery Fee (KES)", placeholder: "e.g. 200", type: "number" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    {f.label}
                  </label>
                  <input
                    required={f.key !== "notes"}
                    type={f.type || "text"}
                    value={createForm[f.key]}
                    onChange={(e) => setCreateForm({ ...createForm, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>
              ))}

              {/* Delivery Type Toggle */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Delivery Type
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsScheduled(false);
                      setCreateForm({ ...createForm, scheduled_at: "" });
                    }}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                      !isScheduled
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    ⚡ Deliver Now
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsScheduled(true)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                      isScheduled
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    🗓️ Schedule
                  </button>
                </div>
              </div>

              {/* Scheduled DateTime Picker */}
              {isScheduled && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Scheduled Date & Time
                  </label>
                  <input
                    required
                    type="datetime-local"
                    value={createForm.scheduled_at}
                    min={new Date(Date.now() + 30 * 60000).toISOString().slice(0, 16)}
                    onChange={(e) => setCreateForm({ ...createForm, scheduled_at: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">Minimum 30 minutes from now</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Notes (optional)
                </label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  placeholder="Any special instructions..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    setCreateError("");
                    setIsScheduled(false);
                  }}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {createLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Creating...
                    </>
                  ) : (
                    "Create Order"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
              Assign Driver
            </h2>
            <p className="text-gray-500 text-sm mb-5">
              Order: <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{showAssign.tracking_token}</span>
            </p>

            {riders.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4">No active drivers available.</p>
            ) : (
              <div className="space-y-2 mb-5">
                {riders.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setSelectedRider(String(r.id))}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${selectedRider === String(r.id)
                        ? "border-orange-400 bg-orange-50"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center text-lg shrink-0">
                      {r.vehicle_type === "motorcycle" ? "🏍️" : r.vehicle_type === "bicycle" ? "🚲" : "🚐"}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{r.name}</p>
                      <p className="text-gray-400 text-xs capitalize">{r.vehicle_type}</p>
                    </div>
                    {selectedRider === String(r.id) && (
                      <svg className="w-4 h-4 text-orange-500 ml-auto" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowAssign(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedRider || assignLoading}
                className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {assignLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Assigning...
                  </>
                ) : (
                  "Assign Driver"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                Order Details
              </h2>
              <button onClick={() => setShowDetail(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { label: "Tracking Token", value: showDetail.tracking_token, mono: true },
                { label: "Customer", value: showDetail.customer_name },
                { label: "Phone", value: showDetail.customer_phone },
                { label: "Address", value: showDetail.address },
                { label: "Items", value: showDetail.items || "—" },
                { label: "Delivery Fee", value: `KES ${Number(showDetail.delivery_fee || 0).toLocaleString()}` },
                { label: "Status", value: showDetail.status?.replace("_", " "), capitalize: true },
                { label: "Driver", value: showDetail.rider?.name || "Not assigned" },
                { label: "Notes", value: showDetail.notes || "—" },
                { label: "Delivery Type", value: showDetail.is_scheduled ? "Scheduled" : "Immediate" },
                ...(showDetail.is_scheduled && showDetail.scheduled_at ? [{
                  label: "Scheduled For",
                  value: new Date(showDetail.scheduled_at).toLocaleString("en-KE", {
                    weekday: "short", month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  }),
                }] : []),
                {
                  label: "Customer Confirmed", value: showDetail.customer_confirmed_at
                    ? new Date(showDetail.customer_confirmed_at).toLocaleString("en-KE")
                    : "Not yet confirmed"
                },
                {
                  label: "Customer Rating", value: showDetail.customer_rating
                    ? "★".repeat(showDetail.customer_rating) + "☆".repeat(5 - showDetail.customer_rating)
                    : "No rating"
                },
                {
                  label: "Customer Feedback", value: showDetail.customer_feedback || "—"
                },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-start gap-4">
                  <span className="text-gray-400 text-sm shrink-0">{row.label}</span>
                  <span
                    className={`text-gray-900 text-sm font-medium text-right ${row.mono ? "font-mono bg-gray-100 px-2 py-0.5 rounded text-xs" : ""
                      } ${row.capitalize ? "capitalize" : ""}`}
                  >
                    {row.value}
                  </span>
                </div>
              ))}

              <button
                onClick={() => {
                  copyTrackingLink(showDetail.tracking_token);
                  setShowDetail(null);
                }}
                className="w-full mt-2 py-2.5 bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-600 rounded-xl text-sm font-semibold transition-colors"
              >
                🔗 Copy Customer Tracking Link
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}